import {Component, signal, inject, OnInit, computed, effect, untracked} from '@angular/core';
import {finalize} from 'rxjs/operators';
import {TrajetDto} from '../shared/dto/trajet.dto';
import {AlertService} from 'sigale-angular';
import {TrajetService} from '../shared/services/trajet.service';
import {ParametrageService} from '../shared/services/parametrage.service';
import {FormGroup, FormControl} from '@angular/forms';
import {AdaptateurEcranService} from '../shared/services/adaptateur-ecran.service';
import {ModeSelecteurPeriode} from '../shared/models/mode-selecteur-periode.enum';
import {DifferencePeriode} from '../shared/models/difference-periode.model';
import {TrajetFiltrageService} from '../shared/services/trajet-filtrage.service';
import {ModeNavette} from '../shared/models/mode-navette.enum';
import {StatutTrajetEnum} from '../gestion-navette/onglets-gestion-navette/trajets/model/statut-trajet.enum';

@Component({
	selector: 'app-reservation-navette',
	templateUrl: './reservation-navette.component.html',
	styleUrl: './reservation-navette.component.scss'
})
export class ReservationNavetteComponent implements OnInit {
	// Constantes
	readonly MODE_NAVETTE = ModeNavette;
	readonly MODE_SELECTEUR_PERIODE = ModeSelecteurPeriode;
	protected NOM_ALERTE_TRAJETS = 'alerteTabReservationTrajet';

	// Injects
	private adapteurEcranService = inject(AdaptateurEcranService);
	private alertService = inject(AlertService);
	private parametrageService = inject(ParametrageService);
	private trajetFiltrageService = inject(TrajetFiltrageService);
	private trajetService = inject(TrajetService);

	// Signaux
	differenceNombreJours = signal<number | null>(null);
	differenceNombreSemaines = signal<number>(0);
	delaiLimiteReservation = signal<number>(0);
	enChargement = signal<boolean>(false);
	sidebarFiltresMobileVisible = signal<boolean>(false);
	sontFiltresActifs = signal<boolean>(false);
	suggestionsLieuxAutocomplete = signal<string[]>([]);
	trajets = signal<TrajetDto[]>([]);
	trajetsFiltres = signal<TrajetDto[]>([]);

	// Variables
	filtresForm: FormGroup;

	// Computed
	estAffichageMobile = computed(() => this.adapteurEcranService.estAffichageMobile());
	messageAucunTrajet = computed(() =>
		this.trajets().length === 0
			? 'GESTION_NAVETTE.TRAJETS.AUCUN_TRAJET'
			: 'GESTION_NAVETTE.TRAJETS.AUCUN_TRAJET_FILTRES'
	);

	constructor() {
		this.filtresForm = new FormGroup({
			afficherTrajetsDisponibles: new FormControl(false),
			afficherReservations: new FormControl(false),
			lieuxDepart: new FormControl([], { nonNullable: true }),
			lieuxArrivee: new FormControl([], { nonNullable: true }),
			heureDepart: new FormControl(null),
			heureArrivee: new FormControl(null),
		});

		this.ecouterChangementsTailleEcran();
		this.verifierFiltresActifs();
	}

	ngOnInit() {
		this.recupererDelaiLimiteReservation();
		this.recupererTrajets();
	}

	/**
	 * Écoute les changements de la taille de l'écran et adapte le composant en conséquence.
	 */
	ecouterChangementsTailleEcran(): void {
		effect(() => {
			this.differenceNombreJours.set(this.adapteurEcranService.estAffichageMobile() ? 0 : null);
			untracked(() => this.recupererTrajets());
		}, { allowSignalWrites: true });
	}

	/**
	 * Vérifie si un filtre est actif et assigne le signal correspondant.
	 */
	verifierFiltresActifs(): void {
		this.filtresForm.valueChanges.subscribe(() => {
			const filtres = this.filtresForm.value;
			this.sontFiltresActifs.set(
				filtres.afficherTrajetsDisponibles ||
				filtres.afficherReservations ||
				(filtres.lieuxDepart?.length ?? 0) > 0 ||
				(filtres.lieuxArrivee?.length ?? 0) > 0 ||
				!!filtres.heureDepart ||
				!!filtres.heureArrivee);
		});
	}

	/**
	 * Change la différence en nombre de semaines et de jours (si on est en format mobile) et récupère les trajets, suite à un changement du sélecteur de période.
	 *
	 * @param periode la période contenant la différence en nombre de semaines et éventuellement en nombre de jours.
	 */
	changerPeriode(periode: DifferencePeriode): void {
		this.differenceNombreSemaines.set(periode.differenceNombreSemaines);
		this.differenceNombreJours.set(periode.differenceNombreJours);
		this.recupererTrajets();
	}

	/**
	 * Récupère les trajets pour la période sélectionnée.
	 */
	recupererTrajets(): void {
		this.enChargement.set(true);
		this.trajetService.recupererTrajets(this.differenceNombreSemaines(), this.differenceNombreJours(), ModeNavette.RESERVATION).pipe(
			finalize(() => this.enChargement.set(false))
		).subscribe({
			next: (trajets: TrajetDto[]) => {
				this.trajets.set(trajets);
				this.appliquerFiltres();
				this.suggestionsLieuxAutocomplete.set(this.trajetFiltrageService.recupererLieuxDepartArrivee(trajets));
			},
			error: (erreur) => {
				this.alertService.clear(this.NOM_ALERTE_TRAJETS);
				this.alertService.error(erreur.error, this.NOM_ALERTE_TRAJETS);
			}
		});
	}

	/**
	 * Récupère le délai limite de réservation d'un trajet après son départ, en minutes.
	 */
	recupererDelaiLimiteReservation(): void {
		this.parametrageService.getDelaiLimiteReservationTrajet().subscribe({
			next: (delai: number) => {
				this.delaiLimiteReservation.set(delai);
			}
		});
	}

	/**
	 * Réinitialise le formulaire de filtrage à ses valeurs par défaut.
	 */
	reinitialiserFiltres(): void {
		this.filtresForm.reset();
		this.trajetsFiltres.set(this.trajets());
	}

	/**
	 * Applique les filtres définis dans le formulaire aux trajets récupérés.
	 */
	appliquerFiltres(): void {
		const trajets = this.trajets();
		const filtres = this.filtresForm.value;
		const statuts = filtres.afficherTrajetsDisponibles ? [StatutTrajetEnum.DISPONIBLE] : [];

		const trajetsFiltres: TrajetDto[] = this.trajetFiltrageService.filtrerTrajetsParCriteresReservation(
			trajets,
			filtres.afficherReservations,
			statuts,
			filtres.lieuxDepart,
			filtres.lieuxArrivee,
			filtres.heureDepart,
			filtres.heureArrivee
		);
		this.sidebarFiltresMobileVisible.set(false);
		this.trajetsFiltres.set(trajetsFiltres);
	}
}
